import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['submitButton', 'submitHint'];

  connect() {
    this.onFocusOut = (event) => this.validateField(event.target);
    this.onInput = (event) => this.fieldChanged(event.target);
    this.onSubmit = (event) => this.submitting(event);
    this.onReset = () => this.reset();

    this.element.addEventListener('focusout', this.onFocusOut);
    this.element.addEventListener('input', this.onInput);
    this.element.addEventListener('change', this.onInput);
    this.element.addEventListener('submit', this.onSubmit);
    this.element.addEventListener('reset', this.onReset);

    // conditional_views.js disables the fields of a hidden block, which takes them out of
    // validation. Watching the attribute keeps the submit gate in step with that.
    this.observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.target !== this.submitButton)) this.refresh();
    });
    this.observer.observe(this.element, {
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    this.refresh();
  }

  disconnect() {
    this.element.removeEventListener('focusout', this.onFocusOut);
    this.element.removeEventListener('input', this.onInput);
    this.element.removeEventListener('change', this.onInput);
    this.element.removeEventListener('submit', this.onSubmit);
    this.element.removeEventListener('reset', this.onReset);
    if (this.observer) this.observer.disconnect();
  }

  get submitButton() {
    return this.hasSubmitButtonTarget ? this.submitButtonTarget : null;
  }

  get fields() {
    return Array.from(this.element.elements).filter(
      (field) => field.willValidate && this.constrained(field),
    );
  }

  constrained(field) {
    return Boolean(
      field.required ||
      field.pattern ||
      field.dataset.confirms ||
      field.dataset.minLength ||
      field.dataset.maxLength,
    );
  }

  // Returns the message for the first rule the field breaks, or null when it passes.
  errorFor(field) {
    if (!field.willValidate || !this.constrained(field)) return null;

    const data = field.dataset;
    const value = field.value.trim();

    if (field.required && value === '') return data.blankMessage;
    if (data.confirms) {
      const source = document.getElementById(data.confirms);
      // Nothing to compare against until the source has a value of its own.
      if (source && source.value !== '' && field.value !== source.value) {
        return data.mismatchMessage;
      }
    }
    if (value !== '') {
      if (field.validity.patternMismatch || field.validity.typeMismatch) {
        return data.invalidMessage;
      }
      if (data.minLength && value.length < Number(data.minLength)) {
        return data.tooShortMessage;
      }
      if (data.maxLength && value.length > Number(data.maxLength)) {
        return data.tooLongMessage;
      }
    }

    return null;
  }

  validateField(field) {
    if (!this.constrained(field)) return;

    const message = this.errorFor(field);
    if (message) {
      this.showError(field, message);
    } else {
      this.clearError(field);
    }
    this.refresh();
  }

  fieldChanged(field) {
    if (this.constrained(field) && !this.errorFor(field))
      this.clearError(field);

    // A field the changed one is compared against needs rechecking too.
    if (field.id) {
      this.element
        .querySelectorAll(`[data-confirms="${field.id}"]`)
        .forEach((dependent) => {
          if (!this.errorFor(dependent)) this.clearError(dependent);
        });
    }

    this.refresh();
  }

  submitting(event) {
    const invalid = this.fields.filter((field) => this.errorFor(field));
    if (invalid.length === 0) return;

    event.preventDefault();
    invalid.forEach((field) => this.showError(field, this.errorFor(field)));
    invalid[0].focus();
  }

  reset() {
    this.fields.forEach((field) => this.clearError(field));
    this.refresh();
  }

  refresh() {
    if (!this.submitButton) return;

    const blocked = this.fields.some((field) => this.errorFor(field));
    this.submitButton.disabled = blocked;
    this.toggleSubmitHint(blocked);
  }

  toggleSubmitHint(blocked) {
    if (!this.hasSubmitHintTarget) return;

    this.submitHintTarget.classList.toggle('submit-hint--blocked', blocked);
  }

  slotFor(field) {
    return field.id
      ? this.element.querySelector(`[data-error-slot="${field.id}"]`)
      : null;
  }

  showError(field, message) {
    const slot = this.slotFor(field);
    if (!slot) return;

    slot.classList.add('field_with_errors');
    this.setMessage(slot, message, 'block');
  }

  clearError(field) {
    const slot = this.slotFor(field);
    if (!slot) return;

    slot.classList.remove('field_with_errors', 'has-error');
    this.setMessage(slot, '', 'none');
  }

  setMessage(slot, message, display) {
    const icon = slot.querySelector('.error-tooltip');
    if (icon) icon.style.display = display;

    const box = slot.querySelector('.error-message');
    if (box) box.textContent = message;
  }
}
